import React from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Flex, Heading } from '@amzn/storm-ui';

import {
  ROLE_ENUM_TO_LABEL_MAP,
  UserRoles,
} from '../../../Shared/constants/userRoles';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import {
  PERMISSION_TRANSLATION_MAP,
  RESTRICTED_PERMISSIONS,
  permissions,
} from '../../constants/permissionConstants';
import { Role } from '../../interfaces/RoleManagementInterfaces';
import { ValidationError } from './EditRole.styles';
import { useEditRole } from './hooks/useEditRole';

interface EditRoleProps {
  userRole: Role;
  onSubmit: (updatedRole: Role) => void;
  onCancel: () => void;
}

export const EditRole: React.FC<EditRoleProps> = ({
  userRole,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { selectedPermission, validationError, handleSubmit, handlePermissions } = useEditRole(userRole, onSubmit, onCancel);

  const roleLabel =
    ROLE_ENUM_TO_LABEL_MAP.find((option) => option.role === userRole.userRole)
      ?.label ?? 'user-role-form-label';

  return (
    <>
      <Heading id="role-management-header" size="medium">
        {t('role-management-header')}
      </Heading>
      <CustomCard title={`${t('edit-role-title')} ${t(roleLabel)}`}>
        {validationError && (
          <ValidationError>{validationError}</ValidationError>
        )}
        {permissions.map((permission) => (
          <Checkbox
            checked={selectedPermission.includes(permission.label)}
            key={`role-checkbox-${permission.id}`}
            id={permission.id}
            positionWithLabel="start"
            label={t(PERMISSION_TRANSLATION_MAP[permission.label])}
            onChange={(event) => {
              handlePermissions(event, permission);
            }}
            disabled={
              RESTRICTED_PERMISSIONS.includes(permission.label) &&
              (userRole.userRole === UserRoles.ACCOUNT_EXECUTIVE ||
                userRole.userRole === UserRoles.ACCOUNT_MANAGER)
            }
          />
        ))}

        <Flex justifyContent="flex-end" padding="xsmall" columnGap="small">
          <CustomButton
            text={t('cancel-button')}
            backgroundColor={CustomButtonColor.cancel}
            handleOnClickCb={onCancel}
          />
          <CustomButton
            text={t('submit-button')}
            backgroundColor={CustomButtonColor.submit}
            handleOnClickCb={handleSubmit}
          />
        </Flex>
      </CustomCard>
    </>
  );
};
